import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Spinner, Form, Button, Table, Alert, Dropdown } from 'react-bootstrap';
import Select from 'react-select';
import * as FaIcons from 'react-icons/fa';

const ErroresTarifariosDetalleComponent = ({ 
    detalleErrores, 
    loading, 
    regionId, 
    regiones, 
    onRegionChange, 
    onSavePrecios, 
    savingPrecios, 
    onBack,
    tiposVehiculo = [], // Nueva prop para tipos de vehículo
    hideHeaderInfo = false // Nueva prop para ocultar información del header cuando se llama desde cierre diario
}) => {
    const [erroresConPrecios, setErroresConPrecios] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filters, setFilters] = useState({});

    // Actualizar errores cuando cambien los datos
    useEffect(() => {
        if (detalleErrores?.errores) {
            setErroresConPrecios(
                detalleErrores.errores.map((error, index) => {
                    // Determinar si es tipo ingreso o egreso
                    const isIngreso = detalleErrores?.tipo_tarifario?.tipo === 'ingreso';
                    
                    // Generar temp_id único incluyendo el index para garantizar unicidad
                    const temp_id = isIngreso 
                        ? `temp_${index}_${error.comuna_id}_${error.servicio_spread_id}_${error.tamano_spread_id}_${error.centro_despacho_id}_${error.cliente_id}_${error.tamano_cliente_id}_${error.local_cliente_id}`
                        : `temp_${index}_${error.comuna_id}_${error.servicio_spread_id}_${error.tamano_spread_id}_${error.centro_despacho_id}`;
                    
                    return {
                        ...error,
                        precio: '',
                        tipo_vehiculo_id: error.tipo_vehiculo_id || '', // Preservar valor del back si existe
                        modalidad: error.modalidad || '', // Preservar valor del back si existe
                        temp_id
                    };
                })
            );
            // Reset filters when data changes
            setFilters({});
        }
    }, [detalleErrores]);

    const handlePrecioChange = (tempId, precio) => {
        setErroresConPrecios(prev => 
            prev.map(error => 
                error.temp_id === tempId 
                    ? { ...error, precio: precio }
                    : error
            )
        );
    };

    // Nueva función para manejar cambios en tipo de vehículo
    const handleTipoVehiculoChange = (tempId, tipoVehiculoId) => {
        setErroresConPrecios(prev => 
            prev.map(error => 
                error.temp_id === tempId 
                    ? { ...error, tipo_vehiculo_id: tipoVehiculoId }
                    : error
            )
        );
    };

    // Nueva función para manejar cambios en modalidad
    const handleModalidadChange = (tempId, modalidad) => {
        setErroresConPrecios(prev => 
            prev.map(error => 
                error.temp_id === tempId 
                    ? { ...error, modalidad: modalidad }
                    : error
            )
        );
    };

    const handleSaveAll = () => {
        // Filtrar solo errores con precio válido
        const erroresValidos = erroresConPrecios.filter(error => {
            const precio = parseInt(error.precio);
            const precioValido = !isNaN(precio) && precio > 0;
            
            // Para errores con TMS "GeoSort" en tarifarios de egreso, también validar tipo de vehículo y modalidad
            const isEgreso = detalleErrores?.tipo_tarifario?.tipo === 'egreso';
            const isGeoSort = error.tms_nombre === 'GeoSort';
            
            if (isEgreso && isGeoSort) {
                const tipoVehiculoValido = error.tipo_vehiculo_id && error.tipo_vehiculo_id !== '';
                const modalidadValida = error.modalidad && error.modalidad !== '';
                return precioValido && tipoVehiculoValido && modalidadValida;
            }
            
            return precioValido;
        });

        if (erroresValidos.length === 0) {
            const isEgreso = detalleErrores?.tipo_tarifario?.tipo === 'egreso';
            const hasGeoSort = erroresConPrecios.some(error => error.tms_nombre === 'GeoSort');
            
            if (isEgreso && hasGeoSort) {
                alert('Debe ingresar precio válido, seleccionar tipo de vehículo y modalidad para registros con TMS GeoSort');
            } else {
                alert('Debe ingresar al menos un precio válido');
            }
            return;
        }

        onSavePrecios(erroresValidos);
    };

    // Función para manejar el sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Función para manejar filtros
    const handleFilterChange = (column, values) => {
        setFilters(prev => ({
            ...prev,
            [column]: values
        }));
    };

    // Obtener valores únicos para filtros
    const getUniqueValues = (column) => {
        const values = erroresConPrecios.map(error => error[column]).filter(Boolean);
        return [...new Set(values)].sort();
    };

    // Aplicar filtros y sorting a los datos
    const filteredAndSortedErrors = useMemo(() => {
        let filtered = [...erroresConPrecios];

        // Aplicar filtros
        Object.keys(filters).forEach(column => {
            if (filters[column] && filters[column].length > 0) {
                filtered = filtered.filter(error => 
                    filters[column].includes(error[column])
                );
            }
        });

        // Aplicar sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [erroresConPrecios, filters, sortConfig]);

    // Componente para header con sorting y filtro
    const SortableFilterableHeader = ({ column, title }) => {
        const uniqueValues = getUniqueValues(column);
        const isActive = sortConfig.key === column;
        const direction = isActive ? sortConfig.direction : null;

        return (
            <th style={{ position: 'relative', minWidth: '180px', whiteSpace: 'nowrap' }}>
                <div className="d-flex align-items-center justify-content-between">
                    <span 
                        onClick={() => handleSort(column)}
                        style={{ cursor: 'pointer', flex: 1 }}
                        className="d-flex align-items-center"
                    >
                        {title}
                        {isActive && (
                            <FaIcons.FaSort 
                                className={`ms-1 ${direction === 'asc' ? 'text-primary' : 'text-secondary'}`}
                                style={{ 
                                    transform: direction === 'desc' ? 'rotate(180deg)' : 'none'
                                }}
                            />
                        )}
                        {!isActive && <FaIcons.FaSort className="ms-1 text-muted" />}
                    </span>
                    
                    <Dropdown>
                        <Dropdown.Toggle 
                            variant="link" 
                            size="sm" 
                            className="p-0 border-0"
                            style={{ color: filters[column]?.length > 0 ? '#0d6efd' : '#6c757d' }}
                        >
                            {/* <FaIcons.FaFilter /> */}
                        </Dropdown.Toggle>

                        <Dropdown.Menu style={{ minWidth: '250px', maxHeight: '300px', overflowY: 'auto' }}>
                            <div className="p-2">
                                <div className="mb-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        onClick={() => handleFilterChange(column, [])}
                                        className="me-2"
                                    >
                                        Limpiar
                                    </Button>
                                </div>
                                {uniqueValues.map(value => (
                                    <div key={value} style={{ fontSize: '0.2rem' }}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`${column}-${value}`}
                                            label={value}
                                            checked={filters[column]?.includes(value) || false}
                                            onChange={(e) => {
                                                const currentValues = filters[column] || [];
                                                const newValues = e.target.checked
                                                    ? [...currentValues, value]
                                                    : currentValues.filter(v => v !== value);
                                                handleFilterChange(column, newValues);
                                            }}
                                            className="mb-1"
                                        />
                                    </div>
                                ))}
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </th>
        );
    };

    const regionOptions = regiones.map(region => ({
        value: region.id,
        label: region.region
    }));

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (!detalleErrores) {
        return (
            <div className="text-center">
                <p>No se encontraron datos de errores</p>
            </div>
        );
    }

    const { tipo_tarifario, total_errores, rango_fecha } = detalleErrores;
    const isIngresoType = tipo_tarifario?.tipo === 'ingreso';

    return (
        <div>
            {/* Header con información - solo mostrar si no se oculta */}
            {!hideHeaderInfo && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    <div>
                                        <h5>{tipo_tarifario?.descripcion}</h5>
                                        <p className="mb-0">
                                            <strong>Total de errores:</strong> {total_errores} | 
                                            <strong> Período:</strong> {rango_fecha?.fecha_inicio} al {rango_fecha?.fecha_fin}
                                        </p>
                                    </div>
                                    <Button variant="outline-secondary" onClick={onBack}>
                                        Volver a Errores
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filtros */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group controlId="regionFilter" style={{ position: 'relative', zIndex: 1000 }}>
                        <Form.Label>Filtrar por Región</Form.Label>
                        <Select
                            options={regionOptions}
                            value={regionOptions.find(opt => opt.value == regionId) || null}
                            onChange={(option) => onRegionChange(option?.value || '')}
                            placeholder="Todas las regiones"
                            isClearable
                            isSearchable
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    position: 'relative',
                                    zIndex: 1001
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    position: 'absolute',
                                    zIndex: 1002
                                })
                            }}
                        />
                    </Form.Group>
                </Col>
                <Col md={8} className="d-flex align-items-end">
                    <Button 
                        variant="success" 
                        onClick={handleSaveAll}
                        disabled={savingPrecios}
                        className="me-2"
                    >
                        {savingPrecios ? 'Guardando...' : 'Guardar Precios'}
                    </Button>
                    <Alert variant="info" className="mb-0 p-2">
                        Ingrese los precios faltantes y haga clic en "Guardar Precios" para crear los tarifarios
                    </Alert>
                </Col>
            </Row>

            {/* Información de filtros aplicados */}
            {Object.keys(filters).length > 0 && (
                <Row className="mb-2">
                    <Col md={12}>
                        <Alert variant="light" className="py-2 mb-0">
                            <small>
                                <strong>Filtros aplicados:</strong> {' '}
                                {Object.entries(filters).map(([column, values]) => 
                                    values.length > 0 && (
                                        <span key={column} className="me-3">
                                            <strong>{column.replace('_nombre', '').replace('_', ' ')}:</strong> {values.join(', ')}
                                        </span>
                                    )
                                )}
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    className="p-0 ms-2"
                                    onClick={() => setFilters({})}
                                >
                                    <small>Limpiar todos</small>
                                </Button>
                            </small>
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Información sobre scroll horizontal */}
            <Row className="mb-2">
                <Col md={12}>
                    <Alert variant="info" className="py-2 mb-0">
                        <small>
                            <FaIcons.FaArrowsAltH className="me-1" />
                            <strong>Tip:</strong> Use el scroll horizontal en la tabla para ver todas las columnas disponibles.
                        </small>
                    </Alert>
                </Col>
            </Row>

            {/* Tabla de errores con scroll horizontal */}
            <div 
                style={{ 
                    maxHeight: '45vh', 
                    overflowY: 'auto', 
                    overflowX: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem'
                }}
            >
                <Table striped bordered hover size="sm" className="mb-0" style={{ minWidth: '1200px' }}>
                    <thead className="sticky-top bg-light">
                        <tr>
                            {isIngresoType ? (
                                <>
                                    <SortableFilterableHeader column="cliente_nombre" title="Cliente" />
                                    <SortableFilterableHeader column="local_cliente_nombre" title="Local Cliente" />
                                    <SortableFilterableHeader column="region_nombre" title="Región" />
                                    <SortableFilterableHeader column="comuna_nombre" title="Comuna" />
                                    <SortableFilterableHeader column="servicio_nombre" title="Servicio Spread" />
                                    <SortableFilterableHeader column="tamano_spread_nombre" title="Tamaño Spread" />
                                    <SortableFilterableHeader column="tamano_cliente_nombre" title="Tamaño Cliente" />
                                    <SortableFilterableHeader column="tms_nombre" title="TMS" />
                                        <th>Precio</th>
                                        <th>Tipo Vehículo</th>
                                        <th>Modalidad</th>
                                    </>
                                ) : (
                                    <>
                                        <SortableFilterableHeader column="region_nombre" title="Región" />
                                        <SortableFilterableHeader column="comuna_nombre" title="Comuna" />
                                        <SortableFilterableHeader column="servicio_nombre" title="Servicio Spread" />
                                        <SortableFilterableHeader column="tamano_spread_nombre" title="Tamaño" />
                                        <SortableFilterableHeader column="centro_despacho_nombre" title="Centro de Despacho" />
                                        <SortableFilterableHeader column="tms_nombre" title="TMS" />
                                        <th>Precio</th>
                                        <th>Tipo Vehículo</th>
                                        <th>Modalidad</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                        {filteredAndSortedErrors.map((error, index) => (
                            <tr key={error.temp_id || `row-${index}`}>
                                {isIngresoType ? (
                                    <>
                                        <td>{error.cliente_nombre}</td>
                                        <td>{error.local_cliente_nombre}</td>
                                        <td>{error.region_nombre}</td>
                                        <td>{error.comuna_nombre}</td>
                                        <td>{error.servicio_nombre}</td>
                                        <td>{error.tamano_spread_nombre}</td>
                                        <td>{error.tamano_cliente_nombre}</td>
                                        <td>{error.tms_nombre}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={error.precio || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Solo permitir números
                                                    if (value === '' || /^\d+$/.test(value)) {
                                                        handlePrecioChange(error.temp_id, value);
                                                    }
                                                }}
                                                placeholder="Precio"
                                                style={{ width: '100px' }}
                                            />
                                        </td>
                                        <td>
                                            {error.tms_nombre === 'GeoSort' ? (
                                                <Form.Select
                                                    size="sm"
                                                    value={error.tipo_vehiculo_id || ''}
                                                    onChange={(e) => handleTipoVehiculoChange(error.temp_id, e.target.value)}
                                                    style={{ width: '140px' }}
                                                >
                                                    <option value="">Seleccionar tipo</option>
                                                    {tiposVehiculo.map(tipo => (
                                                        <option key={tipo.id} value={tipo.id}>
                                                            {tipo.tipo}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            {error.tms_nombre === 'GeoSort' ? (
                                                <Form.Select
                                                    size="sm"
                                                    value={error.modalidad || ''}
                                                    onChange={(e) => handleModalidadChange(error.temp_id, e.target.value)}
                                                    style={{ width: '120px' }}
                                                >
                                                    <option value="">Seleccionar modalidad</option>
                                                    <option value="Fijo">Fijo</option>
                                                    <option value="Variable">Variable</option>
                                                </Form.Select>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{error.region_nombre}</td>
                                        <td>{error.comuna_nombre}</td>
                                        <td>{error.servicio_nombre}</td>
                                        <td>{error.tamano_spread_nombre}</td>
                                        <td>{error.centro_despacho_nombre}</td>
                                        <td>{error.tms_nombre}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={error.precio || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Solo permitir números
                                                    if (value === '' || /^\d+$/.test(value)) {
                                                        handlePrecioChange(error.temp_id, value);
                                                    }
                                                }}
                                                placeholder="Precio"
                                                style={{ width: '100px' }}
                                            />
                                        </td>
                                        <td>
                                            {error.tms_nombre === 'GeoSort' ? (
                                                error.tipo_vehiculo_id && error.tipo_vehiculo_id !== '' ? (
                                                    // Si ya viene tipo_vehiculo_id del back, mostrar como texto
                                                    <span className="text-muted">
                                                        {(() => {
                                                            const tipo = tiposVehiculo.find(t => t.id == error.tipo_vehiculo_id);
                                                            return tipo ? (tipo.tipo || `Tipo Vacio`) : 'Tipo no encontrado';
                                                        })()}
                                                    </span>
                                                ) : (
                                                    // Si no viene, mostrar el selector
                                                    <Form.Select
                                                        size="sm"
                                                        value={error.tipo_vehiculo_id || ''}
                                                        onChange={(e) => handleTipoVehiculoChange(error.temp_id, e.target.value)}
                                                        style={{ width: '140px' }}
                                                    >
                                                        <option value="">Seleccionar tipo</option>
                                                        {tiposVehiculo.map(tipo => (
                                                            <option key={tipo.id} value={tipo.id}>
                                                                {tipo.tipo}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                )
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            {error.tms_nombre === 'GeoSort' ? (
                                                error.modalidad && error.modalidad !== '' ? (
                                                    // Si ya viene modalidad del back, mostrar como texto
                                                    <span className="text-muted">{error.modalidad}</span>
                                                ) : (
                                                    // Si no viene, mostrar el selector
                                                    <Form.Select
                                                        size="sm"
                                                        value={error.modalidad || ''}
                                                        onChange={(e) => handleModalidadChange(error.temp_id, e.target.value)}
                                                        style={{ width: '120px' }}
                                                    >
                                                        <option value="">Seleccionar modalidad</option>
                                                        <option value="Fijo">Fijo</option>
                                                        <option value="Variable">Variable</option>
                                                    </Form.Select>
                                                )
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>

                {filteredAndSortedErrors.length === 0 && erroresConPrecios.length > 0 && (
                    <div className="text-center p-4">
                        <p>No se encontraron errores para los filtros seleccionados</p>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => setFilters({})}
                        >
                            Limpiar filtros
                        </Button>
                    </div>
                )}

                {erroresConPrecios.length === 0 && (
                    <div className="text-center p-4">
                        <p>No se encontraron errores</p>
                    </div>
                )}
            </div>
        </div>
    );
};

ErroresTarifariosDetalleComponent.propTypes = {
    detalleErrores: PropTypes.object,
    loading: PropTypes.bool,
    regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    regiones: PropTypes.array,
    onRegionChange: PropTypes.func.isRequired,
    onSavePrecios: PropTypes.func.isRequired,
    savingPrecios: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
    tiposVehiculo: PropTypes.array, // Nueva prop para tipos de vehículo
    hideHeaderInfo: PropTypes.bool // Nueva prop para ocultar información del header
};

export default ErroresTarifariosDetalleComponent;
